import { apiGet, apiPost } from "../utils/apiClient";

export interface ChatParticipantSummary {
    userId: number;
    displayName: string;
    role: string;
    avatarUrl?: string | null;
    online?: boolean;
}

export interface ChatPresence {
    userId: number;
    online: boolean;
}

export interface ConversationSummary {
    id: number;
    type: string;
    createdAt: string;
    lastMessageAt: string | null;
    lastMessagePreview: string | null;
    otherParticipant: ChatParticipantSummary | null;
    unreadCount?: number;
}

export interface ChatMessage {
    id: number;
    conversationId: number;
    senderId: number;
    senderDisplayName: string;
    body: string;
    createdAt: string;
}

export interface ChatContact {
    userId: number;
    displayName: string;
    role: string;
    avatarUrl?: string | null;
}

export interface UnreadCountResponse {
    count: number;
    conversationIds: number[];
}

export interface SpringPage<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    last: boolean;
}

const TECHNICAL_CHAT_ERROR = /could not execute|record has changed|restarting transaction|hibernate|java\.sql|jdbc|conversation_participants|sql exception|org\.hibernate/i;

export function chatUserFacingMessage(message: string | undefined, fallback: string): string {
    if (!message || !message.trim() || TECHNICAL_CHAT_ERROR.test(message)) {
        return fallback;
    }
    return message.trim();
}

async function readJson<T>(response: Response, fallback: string): Promise<T> {
    if (!response.ok) {
        let message = fallback;
        try {
            const problem = await response.json() as { detail?: string; message?: string };
            message = chatUserFacingMessage(problem.detail || problem.message, fallback);
        } catch {
            // keep fallback
        }
        throw new Error(message);
    }
    return response.json() as Promise<T>;
}

export async function fetchConversations(page = 0, size = 50): Promise<SpringPage<ConversationSummary>> {
    const response = await apiGet(`/api/conversations?page=${page}&size=${size}`);
    return readJson(response, "Unable to load conversations.");
}

export async function fetchContacts(query = "", page = 0, size = 20): Promise<SpringPage<ChatContact>> {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (query.trim()) {
        params.set("q", query.trim());
    }
    const response = await apiGet(`/api/conversations/contacts?${params.toString()}`);
    return readJson(response, "Unable to load people.");
}

export async function createConversation(participantUserId: number): Promise<ConversationSummary> {
    const response = await apiPost("/api/conversations", { participantUserId });
    return readJson(response, "Unable to start that conversation.");
}

export async function fetchMessages(
    conversationId: number,
    before?: string,
    limit = 50,
): Promise<ChatMessage[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (before) {
        params.set("before", before);
    }
    const response = await apiGet(`/api/conversations/${conversationId}/messages?${params.toString()}`);
    return readJson(response, "Unable to load messages.");
}

export async function fetchUnreadCount(): Promise<UnreadCountResponse> {
    const response = await apiGet("/api/conversations/unread");
    return readJson(response, "Unable to load unread messages.");
}

export async function fetchChatAccess(): Promise<{ available: boolean }> {
    const response = await apiGet("/api/conversations/access");
    return readJson(response, "Unable to check Instant Chat access.");
}

export async function markConversationRead(conversationId: number): Promise<void> {
    const response = await apiPost(`/api/conversations/${conversationId}/read`);
    if (!response.ok) {
        return;
    }
}
